import './_styles.scss';
import './_styles.dark-mode.scss';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ButtonGroup, Button } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import qs from 'qs';

import { isEqual } from 'lodash';
import Alert from '../../../../components/Alert';
import CustomTooltip from './components/Chart/CustomTooltip';
import Table from './components/Table';

import { getReportsPaymentsCharts } from '../../../../services/endpoints/reports';
import {
  getReportsPaymentsChartConfigAction,
  setPageFilters,
  setPageFiltersQuery,
  setPageFiltersType,
  setPageLoader,
} from '../../../../redux/actions/reports';
import { formatNumber } from '../../../../utils/helper';
import { checkInterval, setInitPageFilters } from '../../helpers';

let queueTimeout = null;
let cancelToken = axios.CancelToken.source();
let cancelTokenConfig = axios.CancelToken.source();
const Payments = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { darkMode } = useSelector((state) => state.general);
  const { isLoading, viewTabs, mainConfig, pageFiltersQuery, pageConfig: config } = useSelector((state) => state.reports);

  const queryString = qs.parse(location.search.slice(1));

  const [init, setInit] = useState(false);
  const [data, setData] = useState([]);
  const [tableItems, setTableItems] = useState([]);
  const [values, setValues] = useState([]);
  const [error, setError] = useState('');

  const lines = useMemo(() => {
    const computeLineProps = (item) => ({
      type: 'monotone',
      dataKey: 'value',
      dot: false,
      activeDot: { r: 3, stroke: item.color, strokeWidth: 2, fill: darkMode ? '#1b1c20' : 'rgb(255, 255, 255)' },
      data: item.data,
      name: item.title,
      stroke: item.color,
    });

    if (!values?.length || data?.is_empty) {
      const firstItem = data?.chart?.[0] || {};
      const emptyLine = {
        ...firstItem,
        color: '#4D0AE6',
        title: 'Total',
        data: firstItem?.data?.map((item) => ({
          ...item,
          value_formatted: mainConfig?.currency_template?.replace('{{value}}', '0.00'),
          value: 0,
        })),
      };
      return <Line key={emptyLine.title} {...computeLineProps(emptyLine)} />;
    }

    return data?.chart?.filter((item) => values?.includes(item?.title))?.map((item) => <Line key={item.title} {...computeLineProps(item)} />);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainConfig?.currency_template, darkMode, data.chart, data.is_empty, values]);

  const fetchCharts = async (hash) => {
    try {
      const response = await getReportsPaymentsCharts(hash ? { hash } : queryString, cancelToken.token);

      if (response.success) {
        dispatch(setPageFilters(response?.data?.filters));
        dispatch(setPageFiltersType('payments-chart'));
        if (response?.data?.queue?.life_time === 0) {
          clearTimeout(queueTimeout);
          setError('Request life time expired');
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'error') {
          clearTimeout(queueTimeout);
          setError(response?.data?.message ?? 'Something went wrong');
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'completed') {
          setData(response?.data);
          setTableItems(response?.data?.chart);
          setValues(response?.data?.chart?.map((item) => item.title) || []);
          dispatch(setPageLoader(false));
        }
        if (response?.data?.status === 'processing') {
          queueTimeout = setTimeout(() => {
            fetchCharts(response?.data?.queue?.hash);
          }, checkInterval(response?.data?.queue?.request_interval));
        }
      } else {
        setError(response?.data?.message ?? 'Something went wrong');
        hash && dispatch(setPageLoader(false));
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      throw error;
    }
  };

  const getAllData = async () => {
    setError('');
    dispatch(setPageLoader(true));
    try {
      const responseConfig = await dispatch(getReportsPaymentsChartConfigAction(queryString, cancelTokenConfig.token));
      if (responseConfig?.success) {
        if (responseConfig?.data?.queue?.enabled) {
          queueTimeout = setTimeout(() => {
            fetchCharts(responseConfig?.data?.queue?.hash);
          }, checkInterval(responseConfig?.data?.queue?.request_interval));
        } else {
          await fetchCharts();
        }
      }
      if (!init) setInit(true);
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      setError(error?.data?.error_message ?? 'Something went wrong');
      dispatch(setPageLoader(false));
    }
  };

  const onChangeCheck = (e) => {
    const { value, name, checked } = e.target;

    switch (name) {
    case 'all':
      if (name === 'all') {
        if (!checked) {
          setValues([]);
        } else {
          setValues(data?.chart.map((item) => item.title));
        }
      }
      break;

    default:
      if (!checked) {
        setValues((prevState) => prevState.filter((item) => item !== value));
      } else {
        setValues((prevState) => [...prevState, value]);
      }
      break;
    }
  };

  const onSortTable = (head) => {
    if (!head?.code) return;
    setTableItems((prevState) => prevState?.sort((a, b) => {
      if (a[head.code] === null) return 1;
      if (b[head.code] === null) return -1;
      if (a[head.code] === null && b[head.code] === null) return 0;
      if (head.code === 'title') {
        return (
          String(a[head.code]).localeCompare(String(b[head.code]), 'en', {
            numeric: true,
          }) * (head.order === 'asc' ? 1 : -1)
        );
      }
      return head.order === 'asc' ? a[head.code] - b[head.code] : b[head.code] - a[head.code];
    }));
  };

  const cancelQueue = () => {
    clearTimeout(queueTimeout);
    cancelToken.cancel('Cancel request');
    cancelToken = axios.CancelToken.source();
    cancelTokenConfig.cancel('Cancel request');
    cancelTokenConfig = axios.CancelToken.source();
  };

  useEffect(() => {
    document.title = 'Payments';
    cancelQueue();
    getAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    if (!isEqual(queryString, pageFiltersQuery)) {
      dispatch(setPageFiltersQuery(setInitPageFilters('payments', queryString)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(
    () => () => {
      dispatch(setPageFiltersType(''));
      cancelQueue();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <Row>
        <Col xs={12}>
          <div className="reports__nav">
            <ButtonGroup className="reports__nav-tabs">
              {viewTabs?.map((item) => (
                <Button key={item.code} active={item.code === 'chart'} onClick={() => history.push(item.url.replace('/admin', ''))}>
                  {item.title}
                </Button>
              ))}
            </ButtonGroup>
          </div>
        </Col>
      </Row>

      {!isLoading ? (
        <>
          <Row>
            <Col xs={12}>
              {error ? <Alert text={error} /> : null}

              <div className="chart-wrapper">
                <ResponsiveContainer width="100%" height={500}>
                  <LineChart
                    width={700}
                    height={500}
                    data={data?.chart}
                    margin={{
                      top: 5,
                      right: 10,
                      left: 10,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid vertical={false} stroke={darkMode ? '#3f4149' : '#ddd'} />
                    <XAxis
                      dataKey="point"
                      type="category"
                      allowDuplicatedCategory={false}
                      tickLine={false}
                      tickMargin={12}
                      minTickGap={20}
                      interval="equidistantPreserveStart"
                    />
                    <YAxis dataKey="value" axisLine={false} tickLine={false} tickFormatter={(value) => formatNumber(value, 0)} />
                    <Tooltip
                      wrapperStyle={{ pointerEvents: 'auto' }}
                      cursor={{ stroke: darkMode ? '#fff' : '#ccc', strokeDasharray: '4 4' }}
                      content={(props) => <CustomTooltip {...props} config={config} />}
                    />
                    {lines}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Table
                headers={data.table}
                items={tableItems}
                currencyTemplate={mainConfig?.currency_template}
                values={values}
                onChangeCheck={onChangeCheck}
                onSortTable={onSortTable}
              />
            </Col>
          </Row>
        </>
      ) : null}
    </>
  );
};

export default Payments;
